import React, { useState, useEffect, useMemo } from 'react';
import { Package, AlertCircle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductTable } from './components/ProductTable';
import { ProductFilters } from './components/ProductFilters';
import { ProductModal } from './components/ProductModal';
import { ProductPresentationsModal } from './components/ProductPresentationsModal';
import { ProductRecipesModal } from './components/ProductRecipesModal';
import { DeleteConfirmDialog } from './components/DeleteConfirmDialog';
import { ProductLocationsModal } from './components/ProductLocationsModal';
import { Product, ProductFormData, ProductPresentation } from './types/product.types';
import { productsService } from '@/services/products.service';
import { toast } from 'sonner';

// Mock data temporal para TDD - productos sin presentaciones
const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Hamburguesa Clásica',
    description: 'Hamburguesa con carne, lechuga, tomate y queso',
    price: 10.50,
    category: 'hamburguesas',
    presentation: '',
    presentations: [],
    stock: 10,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '2',
    name: 'Sandwich de Pollo',
    description: 'Delicioso sandwich con pechuga de pollo a la parrilla',
    price: 8.90,
    category: 'sandwiches',
    presentation: '',
    presentations: [],
    stock: 15,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '3',
    name: 'Inca Kola 500ml',
    description: 'Gaseosa nacional del Perú',
    price: 3.50,
    category: 'bebidas',
    presentation: '',
    presentations: [],
    stock: 30,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '4',
    name: 'Papas Fritas',
    description: 'Porción de papas fritas doradas',
    price: 4.50,
    category: 'extras',
    presentation: '',
    presentations: [],
    stock: 20,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '5',
    name: 'Cheesecake de Maracuyá',
    description: 'Delicioso postre con sabor tropical peruano',
    price: 6.90,
    category: 'postres',
    presentation: '',
    presentations: [],
    stock: 8,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  }
];

export const ProductsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [isPresentationsModalOpen, setIsPresentationsModalOpen] = useState(false);
  const [productForPresentations, setProductForPresentations] = useState<Product | null>(null);
  const [isRecipesModalOpen, setIsRecipesModalOpen] = useState(false);
  const [productForRecipes, setProductForRecipes] = useState<Product | null>(null);
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState(false);
  const [productForLocations, setProductForLocations] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // Load products from API only once on mount
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productsService.getProducts({
        includeInactive: true,
        limit: 1000 // Cargar todos los productos
      });

      if (response.success) {
        setProducts(response.data);
      } else {
        throw new Error('Error al cargar productos');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // Filter products locally based on filters
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by category
    if (filters.category && filters.category !== 'all') {
      result = result.filter(product =>
        String(product.categoryId) === String(filters.category)
      );
    }

    // Filter by status (active/inactive)
    if (filters.status && filters.status !== 'all') {
      const isActive = filters.status === 'active';
      result = result.filter(product => product.isActive === isActive);
    }

    return result;
  }, [products, filters]);

  const handleOpenCreateModal = () => {
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleDelete = (productId: string) => {
    const product = products.find(p => String(p.id) === String(productId));
    if (product) {
      setProductToDelete(product);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await productsService.deleteProduct(productToDelete.id);
      const action = productToDelete.isActive ? 'desactivado' : 'activado';
      toast.success(`Producto ${action} correctamente`);
      loadProducts(); // Reload products after toggle
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (error) {
      toast.error('Error al cambiar estado del producto');
      console.error('Toggle error:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleSave = async (data: ProductFormData) => {
    try {
      if (selectedProduct) {
        // Update existing product
        await productsService.updateProduct(selectedProduct.id, data);
        toast.success('Producto actualizado correctamente');
      } else {
        // Create new product
        await productsService.createProduct(data);
        toast.success('Producto creado correctamente');
      }
      
      loadProducts(); // Reload products after save
      setIsModalOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      toast.error(selectedProduct ? 'Error al actualizar producto' : 'Error al crear producto');
      console.error('Save error:', error);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleManagePresentations = (product: Product) => {
    setProductForPresentations(product);
    setIsPresentationsModalOpen(true);
  };

  const handleSavePresentations = async (productId: string, presentations: ProductPresentation[]) => {
    try {
      // Note: This is a simplified approach - in a real implementation, 
      // you might want to handle create/update/delete operations individually
      for (const presentation of presentations) {
        // Check if this is a new presentation (generated ID with timestamp vs existing database ID)
        const isNewPresentation = presentation.id.startsWith('pres-') && 
                                  /pres-\d{13}/.test(presentation.id); // Matches pattern pres-<13-digit-timestamp>
                                  
        if (isNewPresentation) {
          // Create new presentation
          await productsService.createPresentation(productId, {
            name: presentation.name,
            description: presentation.description,
            price: presentation.price,
            cost: 0, // Default cost
            isActive: presentation.isActive
          });
        } else {
          // Update existing presentation  
          await productsService.updatePresentation(productId, presentation.id, {
            name: presentation.name,
            description: presentation.description,
            price: presentation.price,
            isActive: presentation.isActive
          });
        }
      }
      
      toast.success('Presentaciones actualizadas correctamente');
      loadProducts(); // Reload products after save
      setIsPresentationsModalOpen(false);
      setProductForPresentations(null);
    } catch (error) {
      toast.error('Error al guardar presentaciones');
      console.error('Save presentations error:', error);
    }
  };

  const handleClosePresentations = () => {
    setIsPresentationsModalOpen(false);
    setProductForPresentations(null);
  };

  // Función para actualizar solo el estado local sin re-guardar en BD
  const handlePresentationsChange = (productId: string, updatedPresentations: ProductPresentation[]) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, presentations: updatedPresentations }
        : product
    ));
    
    // También actualizar el producto en el modal si está abierto
    if (productForPresentations?.id === productId) {
      setProductForPresentations(prev => 
        prev ? { ...prev, presentations: updatedPresentations } : null
      );
    }
  };

  const handleViewRecipes = (product: Product) => {
    setProductForRecipes(product);
    setIsRecipesModalOpen(true);
  };

  const handleCloseRecipes = () => {
    setIsRecipesModalOpen(false);
    setProductForRecipes(null);
  };

  const handleManageLocations = (product: Product) => {
    setProductForLocations(product);
    setIsLocationsModalOpen(true);
  };

  const handleCloseLocations = () => {
    setIsLocationsModalOpen(false);
    setProductForLocations(null);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando productos...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar productos</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Gestión de Productos</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Catálogo de productos y presentaciones del menú
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 bg-macartur-primary hover:bg-macartur-primary/90"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuevo Producto</span>
          </Button>
        </div>
      </div>


      {/* Filters */}
      <ProductFilters 
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Products Table */}
      <div className="bg-white rounded-lg border">
        <ProductTable
          products={filteredProducts}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onManagePresentations={handleManagePresentations}
          onViewRecipes={handleViewRecipes}
          onManageLocations={handleManageLocations}
        />
      </div>

      <ProductModal
        isOpen={isModalOpen}
        product={selectedProduct}
        onSave={handleSave}
        onClose={handleClose}
      />

      <ProductPresentationsModal
        isOpen={isPresentationsModalOpen}
        product={productForPresentations}
        onSave={handleSavePresentations}
        onPresentationsChange={handlePresentationsChange}
        onClose={handleClosePresentations}
      />

      <ProductRecipesModal
        isOpen={isRecipesModalOpen}
        product={productForRecipes}
        onClose={handleCloseRecipes}
      />

      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        productName={productToDelete?.name || ''}
        isActive={productToDelete?.isActive ?? true}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <ProductLocationsModal
        product={productForLocations}
        isOpen={isLocationsModalOpen}
        onClose={handleCloseLocations}
      />
    </div>
  );
};